'use client';

import * as React from 'react';
import Link, { LinkProps } from 'next/link';
import styles from './styles.module.scss';
import Icon, { IconsAvailable } from '@/components/ui/Icon';
import ImageWithFallback from '@/components/ui/ImageWithFallback';
import classNames from 'classnames';

export interface IMenuItemProps extends Omit<LinkProps, 'href' | 'onClick'> {
  title: React.ReactNode;
  href?: LinkProps['href'];
  onClick?: React.MouseEventHandler<HTMLElement>;
  onMouseDown?: React.MouseEventHandler<HTMLElement>;
  active?: boolean;
  iconLeft?: IconsAvailable;
  iconRight?: IconsAvailable;
  imageLeft?: string;
  imageRight?: string;
  disabled?: boolean;
}

const RenderContents: React.FunctionComponent<Pick<IMenuItemProps, 'title' | 'iconLeft' | 'iconRight' | 'imageLeft' | 'imageRight'>> = (
  props,
) => (
  <div className={styles['menu-item__inner']}>
    {props.iconLeft && (
      <Icon
        width={16}
        height={16}
        kind={props.iconLeft}
      />
    )}
    {props.imageLeft && (
      <ImageWithFallback
        src={props.imageLeft}
        icon="money-circle"
        width={16}
        height={16}
        alt=""
        loading="eager"
      />
    )}
    <div className={styles['menu-item__label']}>{props.title}</div>
    {props.iconRight && (
      <Icon
        width={16}
        height={16}
        kind={props.iconRight}
      />
    )}
    {props.imageRight && (
      <ImageWithFallback
        src={props.imageRight}
        icon="money-circle"
        width={16}
        height={16}
        alt=""
      />
    )}
  </div>
);

const MenuItem: React.FunctionComponent<IMenuItemProps> = (props) => {
  const elementRef = React.useRef<HTMLLIElement>(null);

  React.useEffect(() => {
    if (props.active) {
      elementRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }, [props.active]);

  return (
    <li
      role="menu-item"
      className={classNames(styles['menu-item'], { [styles['disabled']]: props.disabled })}
      ref={elementRef}
    >
      {props.href && (
        <Link
          href={props.href}
          onClick={props.onClick}
          onMouseDown={props.onMouseDown}
          prefetch={false}
          draggable={false}
          aria-current={props.active ? 'true' : 'false'}
          aria-disabled={props.disabled}
        >
          <RenderContents
            title={props.title}
            iconLeft={props.iconLeft}
            iconRight={props.iconRight}
            imageLeft={props.imageLeft}
            imageRight={props.imageRight}
          />
        </Link>
      )}
      {!props.href && (
        <button
          type="button"
          className={styles['menu-item']}
          onClick={props.onClick}
          onMouseDown={props.onMouseDown}
          aria-current={props.active ? 'true' : 'false'}
          disabled={props.disabled}
        >
          <RenderContents
            title={props.title}
            iconLeft={props.iconLeft}
            iconRight={props.iconRight}
            imageLeft={props.imageLeft}
            imageRight={props.imageRight}
          />
        </button>
      )}
    </li>
  );
};

MenuItem.displayName = 'MenuItem';

export default MenuItem;
